import axios from 'axios';
import React, {
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useParams } from 'react-router-dom';
import LoadingBox from '../../component/LoadingBox';
import MessageBox from '../../component/MessageBox';
import Container from 'react-bootstrap/Container';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Card from 'react-bootstrap/Card';
import { Store } from '../../Store';
import { getError } from '../../utils';
import { toast } from 'react-toastify';
import { api } from '../../Api';

const reducer = (state, action) => {
  switch (action.type) {
    case 'FETCH_REQUEST':
      return { ...state, loading: true, error: '' };
    case 'FETCH_SUCCESS':
      return { ...state, loading: false, error: '' };
    case 'FETCH_FAIL':
      return { ...state, loading: false, error: action.payload };
    case 'UPLOAD_REQUEST':
      return { ...state, loadingUpload: true, errorUpload: '' };
    case 'UPLOAD_SUCCESS':
      return { ...state, loadingUpload: false, errorUpload: '' };
    case 'UPLOAD_FAIL':
      return { ...state, loadingUpload: false, errorUpload: action.payload };
    default:
      return state;
  }
};

export default function ProductEditScreen() {
  const navigate = useNavigate();
  const { id: productId = 'new' } = useParams();
  const isNewProduct = productId === 'new';
  const { state } = useContext(Store);
  const { userInfo } = state;
  const [{ loading, error, loadingUpload }, dispatch] = useReducer(reducer, {
    loading: !isNewProduct,
    error: '',
    loadingUpload: false,
  });

  const [saving, setSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);

  const [name, setName] = useState('');
  const [price, setPrice] = useState('');
  const [point, setPoint] = useState('');
  const [image, setImage] = useState('');
  const [brand, setBrand] = useState('');
  const [brandMode, setBrandMode] = useState('existing');
  const [brandName, setBrandName] = useState('');
  const [category, setCategory] = useState('');
  const [categoryMode, setCategoryMode] = useState('existing');
  const [categoryName, setCategoryName] = useState('');
  const [stock, setStock] = useState('');
  const [description, setDescription] = useState('');
  const [isPublished, setIsPublished] = useState(true);

  const brandLabel = useMemo(
    () =>
      brandMode === 'custom'
        ? brandName
        : brands.find((item) => item._id === brand)?.brandName || brand,
    [brand, brandMode, brandName, brands]
  );
  const categoryLabel = useMemo(
    () =>
      categoryMode === 'custom'
        ? categoryName
        : categories.find((item) => item._id === category)?.categoryName ||
          category,
    [categories, category, categoryMode, categoryName]
  );

  const authHeader = useMemo(
    () => ({ Authorization: `Bearer ${userInfo.token}` }),
    [userInfo.token]
  );

  const resetForm = () => {
    setName('');
    setPrice('');
    setPoint('');
    setImage('');
    setBrand('');
    setBrandMode('existing');
    setBrandName('');
    setCategory('');
    setCategoryMode('existing');
    setCategoryName('');
    setStock('');
    setDescription('');
    setIsPublished(true);
    setIsDirty(false);
  };

  const markDirty = () => setIsDirty(true);

  useEffect(() => {
    const fetchMeta = async () => {
      try {
        const [brandResult, categoryResult] = await Promise.all([
          axios.get(api + '/api/products/allbrands'),
          axios.get(api + '/api/products/all_categories'),
        ]);
        setBrands(brandResult.data || []);
        setCategories(categoryResult.data || []);
      } catch (err) {
        toast.error('Could not load product categories or brands');
      }
    };
    fetchMeta();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      if (isNewProduct) {
        resetForm();
        dispatch({ type: 'FETCH_SUCCESS' });
        return;
      }

      try {
        dispatch({ type: 'FETCH_REQUEST' });
        const { data } = await axios.get(
          api + `/api/products/admin/${productId}`,
          { headers: authHeader }
        );
        setName(data.name || '');
        setPrice(data.price ?? '');
        setPoint(data.point ?? '');
        setImage(data.image || '');
        setBrand(data.brand || '');
        setBrandMode('existing');
        setBrandName('');
        setCategory(data.category || '');
        setCategoryMode('existing');
        setCategoryName('');
        setStock(data.stock ?? '');
        setDescription(data.description || '');
        setIsPublished(data.isPublished !== false);
        setIsDirty(false);
        dispatch({ type: 'FETCH_SUCCESS' });
      } catch (error) {
        dispatch({ type: 'FETCH_FAIL', payload: getError(error) });
      }
    };
    fetchData();
  }, [authHeader, isNewProduct, productId]);

  useEffect(() => {
    const beforeUnloadHandler = (event) => {
      if (!isDirty) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', beforeUnloadHandler);
    return () => window.removeEventListener('beforeunload', beforeUnloadHandler);
  }, [isDirty]);

  const handleNameChange = (e) => {
    const value = e.target.value;
    setName(value);
    markDirty();
  };

  const validateForm = () => {
    const productPrice = Number(price);
    const productPoint = Number(point);
    const productStock = Number(stock);

    if (!name.trim()) return 'Product name is required';
    if (!image.trim()) return 'Please upload a product photo';
    if (brandMode === 'custom' && !brandName.trim()) {
      return 'Please type the new brand name';
    }
    if (brandMode !== 'custom' && !brand.trim()) {
      return 'Please select a product brand';
    }
    if (categoryMode === 'custom' && !categoryName.trim()) {
      return 'Please type the new category name';
    }
    if (categoryMode !== 'custom' && !category.trim()) {
      return 'Please select a product category';
    }
    if (!Number.isFinite(productPrice) || productPrice < 0) {
      return 'Product price must be zero or more';
    }
    if (!Number.isFinite(productPoint) || productPoint < 0) {
      return 'Product point must be zero or more';
    }
    if (!Number.isInteger(productStock) || productStock < 0) {
      return 'Product stock must be a whole number zero or more';
    }
    return '';
  };

  const buildPayload = () => ({
    name: name.trim(),
    price: Number(price),
    point: Number(point),
    image: image.trim(),
    brand: brandMode === 'custom' ? '' : brand.trim(),
    brandName: brandMode === 'custom' ? brandName.trim() : '',
    category: categoryMode === 'custom' ? '' : category.trim(),
    categoryName: categoryMode === 'custom' ? categoryName.trim() : '',
    stock: Number(stock),
    description: description.trim(),
    isPublished,
  });

  const submitHandler = async (e) => {
    e.preventDefault();
    const validationError = validateForm();
    if (validationError) {
      toast.error(validationError);
      return;
    }

    try {
      setSaving(true);
      const payload = buildPayload();
      if (isNewProduct) {
        await axios.post(api + '/api/products/admin/new', payload, {
          headers: authHeader,
        });
      } else {
        await axios.put(api + `/api/products/${productId}`, payload, {
          headers: authHeader,
        });
      }
      setIsDirty(false);
      toast.success(
        isPublished ? 'Product saved and published.' : 'Product saved as draft.'
      );
      navigate('/admin/products');
    } catch (error) {
      toast.error(getError(error));
    } finally {
      setSaving(false);
    }
  };

  const uploadFileHandler = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    const bodyFormData = new FormData();
    bodyFormData.append('file', file);
    try {
      dispatch({ type: 'UPLOAD_REQUEST' });
      const { data } = await axios.post(api + '/api/upload', bodyFormData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          authorization: `Bearer ${userInfo.token}`,
        },
      });
      dispatch({ type: 'UPLOAD_SUCCESS' });
      setImage(data.secure_url);
      setIsDirty(true);
      toast.success('Image uploaded successfully');
    } catch (err) {
      toast.error(getError(err));
      dispatch({ type: 'UPLOAD_FAIL', payload: getError(err) });
    }
  };

  const cancelHandler = () => {
    if (isDirty && !window.confirm('Discard unsaved product changes?')) return;
    navigate('/admin/products');
  };

  return loading ? (
    <LoadingBox />
  ) : error ? (
    <MessageBox variant="danger">{error}</MessageBox>
  ) : (
    <>
      <Helmet>
        <title>{isNewProduct ? 'Add Product' : 'Edit Product'}</title>
      </Helmet>
      <Container fluid className="my-3">
        <Row className="g-3">
          <Col lg={8}>
            <div className="card mb-3 bg-white">
              <div className="card-header text-center">
                {isNewProduct ? 'Add Product' : 'Edit Product'}
              </div>
              <div className="card-body">
                <Form onSubmit={submitHandler}>
                  <h5 className="mb-3">Basic Info</h5>
                  <Row>
                    <Col md={12}>
                      <Form.Group className="mb-3" controlId="name">
                        <Form.Label>Product Name</Form.Label>
                        <Form.Control
                          value={name}
                          onChange={handleNameChange}
                          required
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3" controlId="category">
                        <Form.Label>Product Category</Form.Label>
                        <Form.Select
                          value={categoryMode === 'custom' ? '__new__' : category}
                          onChange={(e) => {
                            if (e.target.value === '__new__') {
                              setCategoryMode('custom');
                              setCategory('');
                            } else {
                              setCategoryMode('existing');
                              setCategory(e.target.value);
                              setCategoryName('');
                            }
                            markDirty();
                          }}
                          required
                        >
                          <option value="">Select category</option>
                          {category &&
                            !categories.some((item) => item._id === category) && (
                              <option value={category}>{category}</option>
                            )}
                          {categories.map((item) => (
                            <option key={item._id} value={item._id}>
                              {item.categoryName}
                            </option>
                          ))}
                          <option value="__new__">Type new category...</option>
                        </Form.Select>
                        {categoryMode === 'custom' && (
                          <div className="d-flex gap-2 mt-2">
                            <Form.Control
                              value={categoryName}
                              onChange={(e) => {
                                setCategoryName(e.target.value);
                                markDirty();
                              }}
                              placeholder="New category name"
                              autoFocus
                            />
                            <Button
                              type="button"
                              variant="outline-secondary"
                              onClick={() => {
                                setCategoryMode('existing');
                                setCategoryName('');
                                markDirty();
                              }}
                            >
                              List
                            </Button>
                          </div>
                        )}
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3" controlId="brand">
                        <Form.Label>Product Brand</Form.Label>
                        <Form.Select
                          value={brandMode === 'custom' ? '__new__' : brand}
                          onChange={(e) => {
                            if (e.target.value === '__new__') {
                              setBrandMode('custom');
                              setBrand('');
                            } else {
                              setBrandMode('existing');
                              setBrand(e.target.value);
                              setBrandName('');
                            }
                            markDirty();
                          }}
                          required
                        >
                          <option value="">Select brand</option>
                          {brand && !brands.some((item) => item._id === brand) && (
                            <option value={brand}>{brand}</option>
                          )}
                          {brands.map((item) => (
                            <option key={item._id} value={item._id}>
                              {item.brandName}
                            </option>
                          ))}
                          <option value="__new__">Type new brand...</option>
                        </Form.Select>
                        {brandMode === 'custom' && (
                          <div className="d-flex gap-2 mt-2">
                            <Form.Control
                              value={brandName}
                              onChange={(e) => {
                                setBrandName(e.target.value);
                                markDirty();
                              }}
                              placeholder="New brand name"
                              autoFocus
                            />
                            <Button
                              type="button"
                              variant="outline-secondary"
                              onClick={() => {
                                setBrandMode('existing');
                                setBrandName('');
                                markDirty();
                              }}
                            >
                              List
                            </Button>
                          </div>
                        )}
                      </Form.Group>
                    </Col>
                  </Row>

                  <hr />
                  <h5 className="mb-3">Pricing & Inventory</h5>
                  <Row>
                    <Col md={4}>
                      <Form.Group className="mb-3" controlId="price">
                        <Form.Label>Product Price</Form.Label>
                        <Form.Control
                          value={price}
                          type="number"
                          min="0"
                          step="0.01"
                          onChange={(e) => {
                            setPrice(e.target.value);
                            markDirty();
                          }}
                          required
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3" controlId="point">
                        <Form.Label>Product Point</Form.Label>
                        <Form.Control
                          value={point}
                          type="number"
                          min="0"
                          step="0.01"
                          onChange={(e) => {
                            setPoint(e.target.value);
                            markDirty();
                          }}
                          required
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3" controlId="stock">
                        <Form.Label>Product Stock</Form.Label>
                        <Form.Control
                          value={stock}
                          type="number"
                          min="0"
                          step="1"
                          onChange={(e) => {
                            setStock(e.target.value);
                            markDirty();
                          }}
                          required
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <hr />
                  <h5 className="mb-3">Media & Description</h5>
                  <Form.Group className="mb-3" controlId="imageFile">
                    <Form.Label>Product Photo</Form.Label>
                    <Form.Control
                      type="file"
                      accept="image/*"
                      onChange={uploadFileHandler}
                    />
                    <Form.Text muted>
                      Choose a photo. The product photo URL will be saved
                      automatically.
                    </Form.Text>
                    {loadingUpload && <LoadingBox />}
                  </Form.Group>
                  <Form.Group className="mb-3" controlId="description">
                    <Form.Label>Product Description</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={4}
                      value={description}
                      onChange={(e) => {
                        setDescription(e.target.value);
                        markDirty();
                      }}
                    />
                  </Form.Group>

                  <hr />
                  <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 mb-3">
                    <Form.Check
                      type="switch"
                      id="product-published"
                      label={isPublished ? 'Published' : 'Draft'}
                      checked={isPublished}
                      onChange={(e) => {
                        setIsPublished(e.target.checked);
                        markDirty();
                      }}
                    />
                    <div className="d-flex gap-2">
                      <Button
                        type="button"
                        variant="outline-secondary"
                        onClick={cancelHandler}
                      >
                        <i className="fa fa-arrow-left" aria-hidden="true"></i>{' '}
                        Cancel
                      </Button>
                      <Button
                        variant="outline-success"
                        type="submit"
                        disabled={saving || loadingUpload}
                      >
                        <i className="fa fa-save" aria-hidden="true"></i>{' '}
                        {saving ? 'Saving...' : 'Save Product'}
                      </Button>
                    </div>
                  </div>
                </Form>
              </div>
            </div>
          </Col>

          <Col lg={4}>
            <Card className="bg-white">
              <Card.Header className="text-center">Product Preview</Card.Header>
              <Card.Body>
                {image ? (
                  <img
                    src={image}
                    alt={name || 'Product preview'}
                    className="img-fluid rounded mb-3"
                    style={{
                      width: '100%',
                      maxHeight: '260px',
                      objectFit: 'contain',
                      background: '#f8f9fa',
                    }}
                  />
                ) : (
                  <div
                    className="d-flex align-items-center justify-content-center mb-3 rounded"
                    style={{
                      minHeight: '220px',
                      background: '#f8f9fa',
                      color: '#6c757d',
                    }}
                  >
                    Image preview
                  </div>
                )}
                <div className="mb-2">
                  <span
                    className={
                      isPublished ? 'badge bg-success' : 'badge bg-secondary'
                    }
                  >
                    {isPublished ? 'Published' : 'Draft'}
                  </span>
                </div>
                <h5>{name || 'Product name'}</h5>
                <p className="mb-1 text-muted">
                  {brandLabel || 'No brand selected'} /{' '}
                  {categoryLabel || 'No category selected'}
                </p>
                <p className="mb-1">
                  <strong>৳ {price || 0}</strong>
                </p>
                <p className="mb-1">{point || 0} Points</p>
                <p className="mb-3">
                  {Number(stock) > 0 ? `${stock} in stock` : 'Out of stock'}
                </p>
                <p className="mb-0">
                  {description || 'Product description will appear here.'}
                </p>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
}
